import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import {
  IcoChevronRight,
  IcoPlus,
  IcoSpinner,
  IcoTrash,
  IcoUsers,
} from "../../components/icons";
import { BrandedLayout, ConfirmDialog, SlideOver } from "../../components/ui";
import {
  type MemberOption,
  MemberPicker,
} from "../../components/ui/member-picker";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import { type BrandColor, type Group, isAdmin, type User } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type GroupsPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  hasReports: boolean;
  workspaceId: string;
  groups: Group[];
  members: MemberOption[];
};

export async function load(args: LoaderArgs): Promise<GroupsPageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const isOrgAdmin = isAdmin(orgResult.currentMember.role);
  if (!isOrgAdmin) {
    throw new RedirectError(`/orgs/${orgId}`);
  }

  const workspaceId = orgResult.organization.id;
  const groupsResult = await rpc.groups.list({ workspaceId });

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Map members to MemberOption format
  const members: MemberOption[] = orgResult.organization.members
    .filter((m) => m.acceptedAt && !m.archivedAt && !m.leftAt)
    .map((m) => ({
      id: m.id,
      userId: m.userId,
      userName: m.userName,
      userEmail: m.userEmail,
      role: m.role,
    }));

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
    workspaceId,
    groups: groupsResult.groups,
    members,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Groups");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    hasReports,
    workspaceId,
    groups: initialGroups,
    members,
  } = state.value;

  const groupList = useSignal(initialGroups);
  const isCreating = useSignal(false);

  // Edit/Create slide-over state
  const editingGroup = useSignal<Group | null>(null);
  const isNewGroup = useSignal(false);
  const editName = useSignal("");
  const editDescription = useSignal("");
  const editMemberIds = useSignal<string[]>([]);
  const isSaving = useSignal(false);
  const isLoadingMembers = useSignal(false);

  // Delete confirmation state
  const deleteTarget = useSignal<Group | null>(null);
  const isDeleting = useSignal(false);

  async function handleCreate() {
    if (isCreating.value) return;
    isCreating.value = true;

    try {
      const result = await rpc.groups.create({
        workspaceId,
        name: "New Group",
      });
      groupList.value = [
        ...groupList.value,
        { ...result.group, memberCount: 0 },
      ];

      // Open editor for the new group
      editingGroup.value = { ...result.group, memberCount: 0 };
      isNewGroup.value = true;
      editName.value = result.group.name;
      editDescription.value = result.group.description ?? "";
      editMemberIds.value = [];
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isCreating.value = false;
    }
  }

  async function openGroupEditor(group: Group) {
    editingGroup.value = group;
    isNewGroup.value = false;
    editName.value = group.name;
    editDescription.value = group.description ?? "";
    editMemberIds.value = [];

    // Load group members
    isLoadingMembers.value = true;
    try {
      const result = await rpc.groups.getMembers({ groupId: group.id });
      editMemberIds.value = result.members.map((m) => m.memberId);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isLoadingMembers.value = false;
    }
  }

  function closeEditor() {
    editingGroup.value = null;
    isNewGroup.value = false;
    editName.value = "";
    editDescription.value = "";
    editMemberIds.value = [];
  }

  async function handleSave() {
    if (!editingGroup.value || isSaving.value) return;

    isSaving.value = true;
    try {
      // Update group name/description
      await rpc.groups.update({
        groupId: editingGroup.value.id,
        name: editName.value,
        description: editDescription.value || "",
      });

      // Get current members to compute diff
      const currentResult = await rpc.groups.getMembers({
        groupId: editingGroup.value.id,
      });
      const currentMemberIds = new Set(
        currentResult.members.map((m) => m.memberId),
      );
      const newMemberIds = new Set(editMemberIds.value);

      // Members to add
      const toAdd = editMemberIds.value.filter(
        (id) => !currentMemberIds.has(id),
      );
      // Members to remove
      const toRemove = currentResult.members
        .map((m) => m.memberId)
        .filter((id) => !newMemberIds.has(id));

      if (toAdd.length > 0) {
        await rpc.groups.addMembers({
          groupId: editingGroup.value.id,
          memberIds: toAdd,
        });
      }

      if (toRemove.length > 0) {
        await rpc.groups.removeMembers({
          groupId: editingGroup.value.id,
          memberIds: toRemove,
        });
      }

      // Update local state
      groupList.value = groupList.value.map((g) =>
        g.id === editingGroup.value!.id
          ? {
              ...g,
              name: editName.value,
              description: editDescription.value || "",
              memberCount: editMemberIds.value.length,
            }
          : g,
      );

      closeEditor();
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSaving.value = false;
    }
  }

  async function handleDelete() {
    const group = deleteTarget.value;
    if (!group || isDeleting.value) return;

    isDeleting.value = true;
    try {
      await rpc.groups.remove({ groupId: group.id });
      groupList.value = groupList.value.filter((g) => g.id !== group.id);
      deleteTarget.value = null;
      closeEditor();
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isDeleting.value = false;
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="forms"
      isAdmin={true}
      hasReports={hasReports}
    >
      {/* Page header */}
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-text">Groups</h1>
          <p class="text-sm text-text-muted mt-1">
            Organize members for question targeting
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={isCreating.value}
          class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
        >
          {isCreating.value ? (
            <IcoSpinner class="size-4 animate-spin" />
          ) : (
            <IcoPlus class="size-4" />
          )}
          New Group
        </button>
      </div>

      {/* Groups list */}
      {groupList.value.length === 0 ? (
        <EmptyState
          onCreateClick={handleCreate}
          isCreating={isCreating.value}
        />
      ) : (
        <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
          {groupList.value.map((group) => (
            <GroupRow
              key={group.id}
              group={group}
              onClick={() => openGroupEditor(group)}
            />
          ))}
        </div>
      )}

      {/* Edit/Create SlideOver */}
      <SlideOver
        open={editingGroup.value !== null}
        onClose={closeEditor}
        title={isNewGroup.value ? "New Group" : "Edit Group"}
        footer={
          <div class="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                deleteTarget.value = editingGroup.value;
              }}
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              <IcoTrash class="size-4" />
              Delete
            </button>
            <div class="flex gap-2">
              <button
                type="button"
                onClick={closeEditor}
                class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving.value || !editName.value.trim()}
                class="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving.value ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        }
      >
        <div class="flex flex-col gap-6">
          {/* Group Name */}
          <div>
            <label
              htmlFor="group-name"
              class="block text-sm font-medium text-text mb-2"
            >
              Group Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="group-name"
              value={editName.value}
              onInput={(e) => {
                editName.value = (e.target as HTMLInputElement).value;
              }}
              placeholder="e.g., Engineering, Design, New Hires"
              class="w-full px-3 py-2 rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle"
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="group-description"
              class="block text-sm font-medium text-text mb-2"
            >
              Description{" "}
              <span class="text-text-muted font-normal">(optional)</span>
            </label>
            <textarea
              id="group-description"
              value={editDescription.value}
              onInput={(e) => {
                editDescription.value = (e.target as HTMLTextAreaElement).value;
              }}
              placeholder="Add a description for this group..."
              rows={2}
              class="w-full px-3 py-2 rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle resize-none"
            />
          </div>

          {/* Members */}
          <div>
            <label class="block text-sm font-medium text-text mb-2">
              Members
            </label>
            {isLoadingMembers.value ? (
              <div class="flex items-center justify-center py-8">
                <IcoSpinner class="size-6 animate-spin text-text-muted" />
              </div>
            ) : (
              <MemberPicker
                members={members}
                selectedIds={editMemberIds.value}
                onChange={(ids) => {
                  editMemberIds.value = ids;
                }}
              />
            )}
          </div>
        </div>
      </SlideOver>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget.value !== null}
        title="Delete Group"
        confirmLabel={isDeleting.value ? "Deleting..." : "Delete"}
        onConfirm={handleDelete}
        onCancel={() => {
          deleteTarget.value = null;
        }}
      >
        Are you sure you want to delete "{deleteTarget.value?.name}"? Questions
        targeted to this group will become visible to everyone.
      </ConfirmDialog>
    </BrandedLayout>
  );
}

// ============================================================================
// Components
// ============================================================================

function EmptyState({
  onCreateClick,
  isCreating,
}: {
  onCreateClick: () => void;
  isCreating: boolean;
}) {
  return (
    <div class="rounded-xl border border-border bg-surface p-12 text-center">
      <div class="size-14 mx-auto mb-4 rounded-full bg-surface-muted flex items-center justify-center">
        <IcoUsers class="size-7 text-text-muted" />
      </div>
      <h3 class="font-medium text-text mb-2">No groups created yet</h3>
      <p class="text-text-muted mb-6 max-w-sm mx-auto">
        Groups help you target questions to specific team members, like
        "Engineering" or "New Hires".
      </p>
      <button
        type="button"
        onClick={onCreateClick}
        disabled={isCreating}
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
      >
        {isCreating ? (
          <IcoSpinner class="size-4 animate-spin" />
        ) : (
          <IcoPlus class="size-4" />
        )}
        Create your first group
      </button>
    </div>
  );
}

function GroupRow({ group, onClick }: { group: Group; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      class="group flex items-center gap-4 px-5 py-4 hover:bg-surface-hover transition-colors text-left w-full"
    >
      <div class="size-10 rounded-full flex items-center justify-center bg-[var(--brand-100)] text-[var(--brand-600)] ring-2 ring-[var(--brand-200)] flex-shrink-0">
        <IcoUsers class="size-5" />
      </div>
      <div class="flex flex-col min-w-0 flex-1">
        <span class="font-medium text-text group-hover:text-[var(--brand-600)] transition-colors truncate">
          {group.name}
        </span>
        <span class="text-sm text-text-muted">
          {group.memberCount} {group.memberCount === 1 ? "member" : "members"}
          {group.description && (
            <>
              {" "}
              &middot; <span class="truncate">{group.description}</span>
            </>
          )}
        </span>
      </div>
      <IcoChevronRight class="size-5 text-text-muted group-hover:text-[var(--brand-500)] transition-colors flex-shrink-0" />
    </button>
  );
}
